import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { getTopDataOffers } from '../api/apiServices';

// Simple Fisher-Yates shuffle
const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};



export default function TopDataOffers({
  title = "",
  maxItems = 12,
  itemsPerRow = 3,
  showAll = false,
  containerStyle = "",
  cardStyle = "",
  onOfferPress,
  emptyMessage = "No top offers available right now",
  groupByNetwork = true,
  randomize = false,
}) {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchOffers = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getTopDataOffers();

      let rawOffers = [];
      if (Array.isArray(response)) rawOffers = response;
      else if (response?.data && Array.isArray(response.data)) rawOffers = response.data;
      else if (response?.data?.data && Array.isArray(response.data.data)) rawOffers = response.data.data;

      const formatted = rawOffers.map((item) => {
        const summary = item.plan_summary || '';
        const parts = summary.split('|');
        const dataPart = parts[0]?.replace(/^[A-Z]+:\s*/i, '').trim() || 'N/A';

        const price = item.agent_price ?? item.regular_price ?? 0;

        return {
          data: dataPart,
          price: Math.round(price),
          validity: item.validity || 'N/A',
          plan_summary: summary,
          package_code: item.package_code || item.plan_id?.toString() || 'unknown',
          network: item.network_operator?.toUpperCase() || 'UNKNOWN',
          networkLower: item.network_operator?.toLowerCase() || 'unknown',
        };
      });

      // Default sort: cheapest first
      formatted.sort((a, b) => a.price - b.price);

      setOffers(formatted);
    } catch (err) {
      console.error('Failed to load top offers:', err);
      setError('Unable to load offers at the moment');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on mount (you can change to useFocusEffect equivalent if using Next.js + router events)
  useEffect(() => {
    fetchOffers();
  }, [fetchOffers]);

  // ── Prepare visible offers ────────────────────────────────────────
  let visibleOffers = [];

  if (groupByNetwork) {
    const grouped = offers.reduce((acc, offer) => {
      const net = offer.network || 'Other';
      if (!acc[net]) acc[net] = [];
      acc[net].push(offer);
      return acc;
    }, {});

    Object.keys(grouped).forEach((net) => {
      let group = grouped[net];
      if (!showAll) group = group.slice(0, maxItems);
      if (group.length > 0) {
        visibleOffers.push({ network: net, offers: group });
      }
    });
  } else {
    let processed = offers;
    if (randomize) processed = shuffleArray(processed);
    if (!showAll) processed = processed.slice(0, maxItems);

    visibleOffers = [{ network: 'All Offers', offers: processed }];
  }

  // ── Loading / Error / Empty ───────────────────────────────────────
  if (loading) {
    return (
      <div className={`py-10 flex items-center justify-center ${containerStyle}`}>
        <div className="w-8 h-8 border-4 border-[#3866A3] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`py-8 px-4 text-center ${containerStyle}`}>
        <p className="text-red-600 font-medium">{error}</p>
      </div>
    );
  }

  if (visibleOffers.length === 0 || visibleOffers.every((g) => g.offers.length === 0)) {
    return (
      <div className={`py-8 px-4 text-center ${containerStyle}`}>
        <p className="text-gray-500">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <section className={`space-y-5 ${containerStyle}`}>
      <h2 className="text-[20px] font-bold text-[#3866A3] px-1">{title}</h2>

      <div className="px-2">
        {visibleOffers.map((group, groupIndex) => (
          <div key={group.network || groupIndex} className="mb-8">
            {/* Network header (only when grouping) */}
            {groupByNetwork && (
          <h3 className="text-lg font-bold text-gray-800 mb-3 px-1 uppercase tracking-wide">
            {group.network} Offers
          </h3>
        )}

              <div className="grid grid-cols-3 gap-3">
                {group.offers.map((offer, index) => (
            <motion.div
              key={`${offer.package_code}-${index}`}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: index * 0.07 }}
              className={`relative bg-[#3866A3] rounded-2xl p-4 overflow-hidden cursor-pointer 
                         active:scale-[0.97] transition-all hover:shadow-lg ${cardStyle}`}
              onClick={() => onOfferPress?.(offer)}
            >
              {/* Fire badge - smaller */}
              <div className="absolute -top-1 -left-1 w-9 h-9 bg-[#FF383C] rounded-br-2xl flex items-center justify-center">
                <span className="text-2xl drop-shadow">🔥</span>
              </div>

              <div className="flex flex-col items-center mt-7 text-center">
                <p className="text-white font-bold text-lg tracking-tight leading-none">
                  {offer.data}
                </p>

                <p className="text-2xl font-extrabold text-white mt-2 leading-none">
                  ₦{offer.price.toLocaleString()}
                </p>

                <p className="text-gray-200 text-xs mt-2 font-medium">
                  {offer.validity}
                </p>

                {offer.plan_summary && (
                  <p className="text-[10px] text-gray-300 mt-3 opacity-75 line-clamp-1 px-1">
                    {offer.plan_summary.split('|')[1]?.trim() || ''}
                  </p>
                )}
              </div>
            </motion.div>
          ))}
              </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// Optional: Memoized version
export const MemoizedTopDataOffers = React.memo(TopDataOffers);